/**
 * AccountingClosures — Hub de Contabilidad: Empresa · Mensuales · Ejercicios.
 */
import { useCallback, useEffect, useState } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Lock, LockOpen, ShieldAlert, Info, Building2, Calendar, BookOpen, RotateCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MONTH_NAMES = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const fmtPeriod = (y, m) => `${MONTH_NAMES[m]} ${y}`;
const fmtDT = (iso) => iso ? new Date(iso).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : '—';
const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('es-AR') : '—';
const fmtMoney = (n) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(Number(n || 0));

const FY_STATUS_BADGE = {
  open: { label: 'Abierto', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  refunded: { label: 'Refundido (asiento 1)', cls: 'bg-amber-100 text-amber-900 border-amber-300' },
  closed: { label: 'Cerrado', cls: 'bg-rose-100 text-rose-900 border-rose-300' },
};

export default function AccountingClosures() {
  const { user } = useOutletContext();
  const isAdmin = user?.role === 'admin';
  return (
    <div className="p-6 space-y-4" data-testid="accounting-closures-page">
      <div className="flex items-center gap-3">
        <Lock className="w-7 h-7 text-rose-700" />
        <div>
          <h1 className="text-2xl font-bold">Cierres Contables</h1>
          <p className="text-sm text-muted-foreground">Ficha de empresa · Cierres mensuales · Ejercicios contables (asientos de cierre y apertura).</p>
        </div>
      </div>
      <Tabs defaultValue="ejercicios">
        <TabsList>
          <TabsTrigger value="empresa" data-testid="tab-empresa"><Building2 className="w-4 h-4 mr-1" />Empresa</TabsTrigger>
          <TabsTrigger value="ejercicios" data-testid="tab-ejercicios"><BookOpen className="w-4 h-4 mr-1" />Ejercicios contables</TabsTrigger>
          <TabsTrigger value="mensuales" data-testid="tab-mensuales"><Calendar className="w-4 h-4 mr-1" />Cierres mensuales</TabsTrigger>
        </TabsList>
        <TabsContent value="empresa" className="mt-4"><EmpresaTab isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="ejercicios" className="mt-4"><EjerciciosTab isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="mensuales" className="mt-4"><MensualesTab isAdmin={isAdmin} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ==================== EMPRESA ====================

function EmpresaTab({ isAdmin }) {
  const [form, setForm] = useState({
    razon_social: '', domicilio_legal: '', domicilio_fiscal: '', cuit: '',
    fiscal_year_end_day: 31, fiscal_year_end_month: 12, personeria_juridica: '',
    account_res_ejercicio: '3.1.3.01.000', account_res_no_asignados: '3.1.3.02.000',
  });
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/accounting/company-config`, { withCredentials: true });
      if (r.data && r.data.razon_social) setForm(prev => ({ ...prev, ...r.data }));
    } catch (e) { /* silencioso */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!form.razon_social) { toast.error('Razón Social obligatoria'); return; }
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/accounting/company-config`, form, { withCredentials: true });
      toast.success('Ficha de empresa guardada');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); } finally { setSaving(false); }
  };
  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        <div className="grid gap-3 md:grid-cols-2">
          <div><Label>Razón Social *</Label><Input value={form.razon_social} onChange={e => setForm({ ...form, razon_social: e.target.value })} data-testid="cc-razon-social" /></div>
          <div><Label>CUIT</Label><Input value={form.cuit} onChange={e => setForm({ ...form, cuit: e.target.value })} data-testid="cc-cuit" placeholder="XX-XXXXXXXX-X" /></div>
          <div><Label>Domicilio Legal</Label><Input value={form.domicilio_legal} onChange={e => setForm({ ...form, domicilio_legal: e.target.value })} data-testid="cc-domicilio-legal" /></div>
          <div><Label>Domicilio Fiscal</Label><Input value={form.domicilio_fiscal} onChange={e => setForm({ ...form, domicilio_fiscal: e.target.value })} data-testid="cc-domicilio-fiscal" /></div>
          <div>
            <Label>Fecha de cierre contable (DD/MM)</Label>
            <div className="flex gap-2">
              <Input type="number" min="1" max="31" value={form.fiscal_year_end_day} onChange={e => setForm({ ...form, fiscal_year_end_day: Number(e.target.value) })} data-testid="cc-fy-day" />
              <select className="border rounded h-10 px-2 text-sm w-full" value={form.fiscal_year_end_month} onChange={e => setForm({ ...form, fiscal_year_end_month: Number(e.target.value) })} data-testid="cc-fy-month">
                {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{MONTH_NAMES[m]}</option>)}
              </select>
            </div>
          </div>
          <div><Label>N° Registro Personería Jurídica</Label><Input value={form.personeria_juridica} onChange={e => setForm({ ...form, personeria_juridica: e.target.value })} data-testid="cc-personeria" /></div>
          <div><Label>Cuenta Resultado del Ejercicio</Label><Input value={form.account_res_ejercicio} onChange={e => setForm({ ...form, account_res_ejercicio: e.target.value })} data-testid="cc-acc-resej" /></div>
          <div><Label>Cuenta Resultados No Asignados</Label><Input value={form.account_res_no_asignados} onChange={e => setForm({ ...form, account_res_no_asignados: e.target.value })} data-testid="cc-acc-rna" /></div>
        </div>
        {isAdmin && (
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="cc-save">{saving ? 'Guardando…' : 'Guardar ficha'}</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ==================== EJERCICIOS ====================

function EjerciciosTab({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [openDialogFor, setOpenDialogFor] = useState(null);  // { fy, step: 'refundicion'|'cierre-patrimonial'|'apertura' }
  const [preview, setPreview] = useState(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [showOpen, setShowOpen] = useState(false);
  const [openForm, setOpenForm] = useState({ number: 1, start_date: '', end_date: '' });
  const [reopenTarget, setReopenTarget] = useState(null);
  const [reopenReason, setReopenReason] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/accounting/fiscal-years`, { withCredentials: true });
      setItems(r.data.items || []);
      setOpenForm(prev => ({ ...prev, number: (r.data.items?.[0]?.number || 0) + 1 }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cargar ejercicios'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openStep = async (fy, step) => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/accounting/fiscal-years/${fy.fiscal_year_id}/preview/${step}`, { withCredentials: true });
      setPreview(r.data);
      setOpenDialogFor({ fy, step });
      setNotes('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error en preview'); }
  };

  const confirmStep = async () => {
    if (!openDialogFor) return;
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/accounting/fiscal-years/${openDialogFor.fy.fiscal_year_id}/post/${openDialogFor.step}`,
        { notes: notes || null }, { withCredentials: true });
      toast.success('Asiento posteado');
      setOpenDialogFor(null); setPreview(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al postear'); } finally { setSaving(false); }
  };

  const openFy = async () => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/accounting/fiscal-years/open`, openForm, { withCredentials: true });
      toast.success(`Ejercicio N° ${openForm.number} abierto`);
      setShowOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo abrir'); } finally { setSaving(false); }
  };

  const reopen = async () => {
    if (!reopenReason || reopenReason.trim().length < 5) { toast.error('Motivo obligatorio ≥5 caracteres'); return; }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/accounting/fiscal-years/${reopenTarget.fiscal_year_id}/reopen`,
        { reason: reopenReason.trim() }, { withCredentials: true });
      toast.success('Ejercicio reabierto');
      setReopenTarget(null); setReopenReason('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo reabrir'); } finally { setSaving(false); }
  };

  const stepLabel = { refundicion: '1. Refundición de resultados', 'cierre-patrimonial': '2. Cierre patrimonial', apertura: '3. Apertura ejercicio siguiente' };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Ejercicios contables</CardTitle>
        {isAdmin && <Button size="sm" onClick={() => setShowOpen(true)} data-testid="btn-open-fy">Abrir ejercicio nuevo</Button>}
      </CardHeader>
      <CardContent>
        <Table data-testid="fy-table">
          <TableHeader><TableRow>
            <TableHead>N°</TableHead>
            <TableHead>Desde</TableHead>
            <TableHead>Hasta</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead className="text-right">Resultado</TableHead>
            <TableHead className="text-right w-96">Acciones</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No hay ejercicios abiertos</TableCell></TableRow>}
            {items.map(fy => {
              const s = FY_STATUS_BADGE[fy.status] || { label: fy.status, cls: '' };
              return (
                <TableRow key={fy.fiscal_year_id} data-testid={`fy-row-${fy.number}`}>
                  <TableCell className="font-bold">N° {fy.number}</TableCell>
                  <TableCell>{fmtDate(fy.start_date)}</TableCell>
                  <TableCell>{fmtDate(fy.end_date)}</TableCell>
                  <TableCell><Badge className={s.cls}>{s.label}</Badge></TableCell>
                  <TableCell className={`text-right font-mono text-sm ${fy.result_of_period > 0 ? 'text-emerald-700' : fy.result_of_period < 0 ? 'text-rose-700' : ''}`}>
                    {fy.result_of_period != null ? fmtMoney(fy.result_of_period) : '—'}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    {isAdmin && fy.status === 'open' && (
                      <Button size="sm" onClick={() => openStep(fy, 'refundicion')} data-testid={`fy-${fy.number}-refundicion`}>1. Refundición</Button>
                    )}
                    {isAdmin && fy.status === 'refunded' && (
                      <Button size="sm" onClick={() => openStep(fy, 'cierre-patrimonial')} data-testid={`fy-${fy.number}-cierre`}>2. Cierre patrimonial</Button>
                    )}
                    {isAdmin && fy.status === 'closed' && !fy.apertura_entry_id && (
                      <Button size="sm" variant="outline" onClick={() => openStep(fy, 'apertura')} data-testid={`fy-${fy.number}-apertura`}>3. Apertura</Button>
                    )}
                    {isAdmin && (fy.status === 'refunded' || fy.status === 'closed') && (
                      <Button size="sm" variant="ghost" className="text-emerald-700" onClick={() => setReopenTarget(fy)} data-testid={`fy-${fy.number}-reopen`}>
                        <RotateCw className="w-3.5 h-3.5 mr-1" />Reabrir
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>

      {/* Diálogo: preview + confirm de asiento */}
      <Dialog open={!!openDialogFor} onOpenChange={(o) => { if (!o) { setOpenDialogFor(null); setPreview(null); } }}>
        <DialogContent className="max-w-4xl" data-testid="fy-step-dialog">
          <DialogHeader>
            <DialogTitle>{openDialogFor && stepLabel[openDialogFor.step]}</DialogTitle>
            <DialogDescription>Preview del asiento — revisá Debe/Haber antes de confirmar. Se posteará en el Libro Diario con la fecha indicada.</DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground">
                Fecha del asiento: <strong>{fmtDate(preview.date)}</strong>
                {preview.result_of_period != null && <> · Resultado del período: <strong className={preview.result_of_period > 0 ? 'text-emerald-700' : 'text-rose-700'}>{fmtMoney(preview.result_of_period)}</strong></>}
              </div>
              <div className="max-h-80 overflow-auto border rounded">
                <table className="w-full text-xs">
                  <thead className="bg-muted sticky top-0">
                    <tr><th className="text-left p-2">Cuenta</th><th className="text-left p-2">Nombre</th><th className="text-right p-2">Debe</th><th className="text-right p-2">Haber</th></tr>
                  </thead>
                  <tbody>
                    {preview.lines.map((l, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-2 font-mono">{l.account_code}</td>
                        <td className="p-2">{l.account_name}</td>
                        <td className="p-2 text-right font-mono">{l.debit ? fmtMoney(l.debit) : ''}</td>
                        <td className="p-2 text-right font-mono">{l.credit ? fmtMoney(l.credit) : ''}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 font-bold bg-muted">
                      <td className="p-2" colSpan={2}>TOTALES</td>
                      <td className="p-2 text-right font-mono">{fmtMoney(preview.total_debit)}</td>
                      <td className="p-2 text-right font-mono">{fmtMoney(preview.total_credit)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {Math.abs(preview.total_debit - preview.total_credit) > 0.02 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 flex gap-2">
                  <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>El asiento no cuadra por <strong>{fmtMoney(preview.total_debit - preview.total_credit)}</strong>. Al confirmar el sistema rechazará el posteo. Causa típica: falta el balance de apertura del ejercicio (saldos iniciales). Cargá un asiento manual con los saldos previos antes de intentar de nuevo.</div>
                </div>
              )}
              <div>
                <Label className="text-xs">Notas (opcional)</Label>
                <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Referencias, observaciones del auditor…" data-testid="fy-step-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpenDialogFor(null); setPreview(null); }}>Cancelar</Button>
            <Button onClick={confirmStep} disabled={saving || (preview && Math.abs(preview.total_debit - preview.total_credit) > 0.02)} data-testid="fy-step-confirm">
              {saving ? 'Posteando…' : 'Confirmar y postear asiento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo: abrir ejercicio */}
      <Dialog open={showOpen} onOpenChange={setShowOpen}>
        <DialogContent data-testid="fy-open-dialog">
          <DialogHeader><DialogTitle>Abrir Ejercicio N° {openForm.number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>N° de ejercicio</Label><Input type="number" value={openForm.number} onChange={e => setOpenForm({ ...openForm, number: Number(e.target.value) })} data-testid="fy-open-number" /></div>
              <div></div>
              <div><Label>Desde</Label><Input type="date" value={openForm.start_date} onChange={e => setOpenForm({ ...openForm, start_date: e.target.value })} data-testid="fy-open-from" /></div>
              <div><Label>Hasta</Label><Input type="date" value={openForm.end_date} onChange={e => setOpenForm({ ...openForm, end_date: e.target.value })} data-testid="fy-open-to" /></div>
            </div>
            <div className="rounded-md border border-blue-300 bg-blue-50 p-3 text-xs text-blue-900 flex gap-2">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>Configurá la fecha de cierre contable en la ficha de <strong>Empresa</strong> para que el sistema pueda sugerir automáticamente el rango en próximos ejercicios.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOpen(false)}>Cancelar</Button>
            <Button onClick={openFy} disabled={saving} data-testid="fy-open-confirm">Abrir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo: reabrir */}
      <Dialog open={!!reopenTarget} onOpenChange={(o) => { if (!o) { setReopenTarget(null); setReopenReason(''); } }}>
        <DialogContent data-testid="fy-reopen-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><LockOpen className="w-5 h-5 text-emerald-700" />Reabrir Ejercicio N° {reopenTarget?.number}</DialogTitle>
            <DialogDescription>Se generarán contra-asientos automáticos de todos los asientos posteados. Motivo obligatorio.</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Motivo</Label>
            <Textarea value={reopenReason} onChange={e => setReopenReason(e.target.value)} className="min-h-[100px]" data-testid="fy-reopen-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenTarget(null)}>Cancelar</Button>
            <Button className="bg-emerald-700 hover:bg-emerald-800" onClick={reopen} disabled={saving} data-testid="fy-reopen-confirm">
              {saving ? 'Reabriendo…' : 'Reabrir ejercicio'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ==================== MENSUALES (lo que teníamos) ====================

function MensualesTab({ isAdmin }) {
  const today = new Date();
  const defaultMonth = today.getMonth() === 0 ? 12 : today.getMonth();
  const defaultYear = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeMode, setCloseMode] = useState('single');
  const [closeForm, setCloseForm] = useState({ year: defaultYear, month: defaultMonth, to_year: defaultYear, to_month: defaultMonth, notes: '' });
  const [reopenTarget, setReopenTarget] = useState(null);
  const [reopenReason, setReopenReason] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/accounting/closures?scope=all`, { withCredentials: true });
      setItems(r.data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const closePeriod = async () => {
    setSaving(true);
    try {
      if (closeMode === 'range') {
        const from = Number(closeForm.year) * 12 + Number(closeForm.month) - 1;
        const to = Number(closeForm.to_year) * 12 + Number(closeForm.to_month) - 1;
        if (from > to) { toast.error('Desde debe ser <= Hasta'); setSaving(false); return; }
        const r = await axios.post(`${BACKEND_URL}/api/accounting/closures/bulk`, {
          from_year: Number(closeForm.year), from_month: Number(closeForm.month),
          to_year: Number(closeForm.to_year), to_month: Number(closeForm.to_month),
          scope: 'all', notes: closeForm.notes || null,
        }, { withCredentials: true });
        toast.success(`Rango cerrado: ${r.data.created_count} nuevos, ${r.data.skipped_count} ya cerrados`);
      } else {
        await axios.post(`${BACKEND_URL}/api/accounting/closures`, {
          period_year: Number(closeForm.year), period_month: Number(closeForm.month),
          scope: 'all', notes: closeForm.notes || null,
        }, { withCredentials: true });
        toast.success(`Período ${fmtPeriod(closeForm.year, closeForm.month)} cerrado`);
      }
      setCloseOpen(false); setCloseForm({ ...closeForm, notes: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setSaving(false); }
  };

  const reopen = async () => {
    if (!reopenReason || reopenReason.trim().length < 5) { toast.error('Motivo obligatorio'); return; }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/accounting/closures/${reopenTarget.closure_id}/reopen`, { reason: reopenReason }, { withCredentials: true });
      toast.success('Reabierto');
      setReopenTarget(null); setReopenReason(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setSaving(false); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Cierres mensuales (contable general)</CardTitle>
        {isAdmin && <Button size="sm" className="bg-rose-700 hover:bg-rose-800" onClick={() => setCloseOpen(true)} data-testid="btn-close-period"><Lock className="w-4 h-4 mr-1" />Cerrar período</Button>}
      </CardHeader>
      <CardContent>
        {loading ? <p className="text-center py-8 text-muted-foreground">Cargando…</p> : (
          <Table data-testid="closures-table">
            <TableHeader><TableRow>
              <TableHead>Período</TableHead><TableHead>Estado</TableHead>
              <TableHead>Cerrado</TableHead><TableHead>Reapertura</TableHead>
              <TableHead>Notas / Motivo</TableHead><TableHead className="w-40 text-right">Acción</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Todavía no hay períodos cerrados</TableCell></TableRow>}
              {items.map(c => (
                <TableRow key={c.closure_id} data-testid={`closure-row-${c.period_year}-${c.period_month}`}>
                  <TableCell className="font-medium">{fmtPeriod(c.period_year, c.period_month)}</TableCell>
                  <TableCell>{c.status === 'closed'
                    ? <Badge className="bg-amber-100 text-amber-900 border-amber-300"><Lock className="w-3 h-3 mr-1" />Cerrado</Badge>
                    : <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300"><LockOpen className="w-3 h-3 mr-1" />Reabierto</Badge>}</TableCell>
                  <TableCell className="text-xs"><div>{fmtDT(c.closed_at)}</div><div className="text-muted-foreground">{c.closed_by_name}</div></TableCell>
                  <TableCell className="text-xs">{c.reopened_at ? (<><div>{fmtDT(c.reopened_at)}</div><div className="text-muted-foreground">{c.reopened_by_name}</div></>) : '—'}</TableCell>
                  <TableCell className="text-xs max-w-md">{c.reopened_reason && <div className="text-amber-800"><strong>Reapertura:</strong> {c.reopened_reason}</div>}{c.notes && <div className="text-muted-foreground">{c.notes}</div>}</TableCell>
                  <TableCell className="text-right">{c.status === 'closed' && isAdmin && (
                    <Button size="sm" variant="outline" onClick={() => setReopenTarget(c)} data-testid={`btn-reopen-${c.period_year}-${c.period_month}`}><LockOpen className="w-4 h-4 mr-1" />Reabrir</Button>
                  )}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent data-testid="close-period-dialog">
          <DialogHeader><DialogTitle>Cerrar período (contable general)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="inline-flex rounded-md border p-0.5 text-sm">
              <button type="button" onClick={() => setCloseMode('single')} data-testid="mode-single" className={`px-3 py-1 rounded ${closeMode === 'single' ? 'bg-primary text-primary-foreground' : ''}`}>Mensual</button>
              <button type="button" onClick={() => setCloseMode('range')} data-testid="mode-range" className={`px-3 py-1 rounded ${closeMode === 'range' ? 'bg-primary text-primary-foreground' : ''}`}>Rango de meses</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{closeMode === 'range' ? 'Desde Año' : 'Año'}</Label><Input type="number" value={closeForm.year} onChange={e => setCloseForm({ ...closeForm, year: e.target.value })} data-testid="close-year" /></div>
              <div><Label>{closeMode === 'range' ? 'Desde Mes' : 'Mes'}</Label>
                <select className="w-full border rounded px-2 h-9" value={closeForm.month} onChange={e => setCloseForm({ ...closeForm, month: e.target.value })} data-testid="close-month">
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{MONTH_NAMES[m]}</option>)}
                </select></div>
            </div>
            {closeMode === 'range' && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Hasta Año</Label><Input type="number" value={closeForm.to_year} onChange={e => setCloseForm({ ...closeForm, to_year: e.target.value })} data-testid="close-to-year" /></div>
                <div><Label>Hasta Mes</Label>
                  <select className="w-full border rounded px-2 h-9" value={closeForm.to_month} onChange={e => setCloseForm({ ...closeForm, to_month: e.target.value })} data-testid="close-to-month">
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{MONTH_NAMES[m]}</option>)}
                  </select></div>
              </div>
            )}
            <div><Label>Notas (opcional)</Label><Textarea value={closeForm.notes} onChange={e => setCloseForm({ ...closeForm, notes: e.target.value })} data-testid="close-notes" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCloseOpen(false)}>Cancelar</Button>
            <Button onClick={closePeriod} disabled={saving} className="bg-rose-700 hover:bg-rose-800" data-testid="close-confirm">{saving ? 'Cerrando…' : (closeMode === 'range' ? 'Cerrar rango' : 'Cerrar período')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reopenTarget} onOpenChange={(o) => { if (!o) { setReopenTarget(null); setReopenReason(''); } }}>
        <DialogContent><DialogHeader><DialogTitle>Reabrir período</DialogTitle></DialogHeader>
          <div><Label>Motivo</Label><Textarea value={reopenReason} onChange={e => setReopenReason(e.target.value)} className="min-h-[100px]" data-testid="reopen-reason" /></div>
          <DialogFooter><Button variant="outline" onClick={() => setReopenTarget(null)}>Cancelar</Button>
            <Button onClick={reopen} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="reopen-confirm">Reabrir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
